import { useState, useMemo } from "react";
import { Eye, Flag, SlidersHorizontal } from "lucide-react";
import type { AdminOrg } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { AddToast } from "../../components/feedback/Toast";
import { OrgAvatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterDrawer, FilterFieldDef } from "../../components/ui/FilterDrawer";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchBar } from "../../components/ui/SearchBar";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Organisations list ───────────────────────────────────────────────────────

export function OrgsView({ onDetail, addToast }: { onDetail: (o: AdminOrg) => void; addToast: AddToast }) {
  const { ADMIN_ORGS, PLATFORM_STATS } = useAdminData();
  const [search, setSearch] = useState("");
  const [kybFilter, setKybFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const orgFilterFields: FilterFieldDef[] = [
    { type: "chips", key: "kyb", label: "KYB", options: ["All", "Verified", "Pending", "Unverified", "Rejected"] },
    { type: "chips", key: "status", label: "Status", options: ["All", "Active", "Suspended", "Inactive"] },
  ];
  const hasFilter = kybFilter !== "All" || statusFilter !== "All";

  const filtered = useMemo(() => ADMIN_ORGS.filter((o) => {
    const s = search.toLowerCase();
    const matchSearch = !s || o.name.toLowerCase().includes(s) || o.industry.toLowerCase().includes(s);
    const matchKyb = kybFilter === "All" || o.kyb === kybFilter;
    const matchStatus = statusFilter === "All" || o.account === statusFilter;
    return matchSearch && matchKyb && matchStatus;
  }), [ADMIN_ORGS, search, kybFilter, statusFilter]);

  return (
    <div>
      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Organisation" fields={orgFilterFields}
        values={{ kyb: kybFilter, status: statusFilter }}
        onApply={(v) => { setKybFilter(v.kyb ?? "All"); setStatusFilter(v.status ?? "All"); }} />

      <PageHeader title="Organisations" sub={`${PLATFORM_STATS.totalOrgs.toLocaleString()} registered organisations`} />
      <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search organisations…" />
        <button className="filter-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${hasFilter ? CORAL : T.border}`, background: hasFilter ? `rgba(209,0,75,0.06)` : T.s2, color: hasFilter ? CORAL : T.sub, transition: "all 0.15s" }}>
          <SlidersHorizontal size={13} /> Filters
        </button>
        {hasFilter && (
          <button onClick={() => { setKybFilter("All"); setStatusFilter("All"); }} style={{ fontSize: "11px", color: CORAL, fontFamily: F, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>
        )}
      </div>
      <Card>
        <TableWrap>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F, minWidth: "960px" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Organisation", "Industry", "KYB", "Status", "Wallet", "Escrow", "Fee", "Postings", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <OrgAvatar initials={o.initials} color={o.color} size={30} />
                      <div>
                        <p style={{ fontSize: "12px", fontWeight: 600, color: T.text }}>{o.name}</p>
                        <p style={{ fontSize: "10px", color: T.sub }}>{o.location}</p>
                      </div>
                      {o.flagged && <Flag size={11} style={{ color: "#991b1b" }} />}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "11px", color: T.sub }}>{o.industry}</td>
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={o.kyb} /></td>
                  <td style={{ padding: "12px 14px" }}><StatusBadge status={o.account} /></td>
                  <td style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: o.walletBalance === "SAR 0" ? T.muted : "#166534", fontFamily: F }}>{o.walletBalance}</p>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: "11px", color: o.escrowHeld === "SAR 0" ? T.muted : "#1e40af", fontFamily: F }}>{o.escrowHeld}</p>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {o.customFee !== undefined
                      ? <span style={{ fontSize: "11px", color: "#1e40af", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>{o.customFee}%</span>
                      : <span style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>Default</span>}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text }}>{o.activePostings}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <Btn variant="ghost" size="xs" onClick={() => onDetail(o)}><Eye size={11} /> View</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>
    </div>
  );
}
